import { HStack, Text } from '@chakra-ui/react';
import { formatShortDate } from '@duke-hq/djs';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Ban, ClipboardCheck, ClipboardX, Users, type LucideIcon } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import type { EmployerStatus } from '@duke-hq/shared-constants/employer';

type StatusTone = 'success' | 'info' | 'warning' | 'danger';

const COLOR_SCHEME_FOR_TONE: Record<StatusTone, string> = {
  success: 'green',
  info: 'blue',
  warning: 'orange',
  danger: 'red',
};

type StatusPresentation = { tone: StatusTone; icon: LucideIcon };

const STATUS_PRESENTATION: Record<EmployerStatus, StatusPresentation> = {
  READY: { tone: 'success', icon: ClipboardCheck },
  DOCS_INCOMPLETE: { tone: 'info', icon: ClipboardX },
  AT_CAPACITY: { tone: 'warning', icon: Users },
  DO_NOT_USE: { tone: 'danger', icon: Ban },
};

export function employerStatusShortLabel(status: EmployerStatus): string {
  switch (status) {
    case 'READY':
      return t`Ready`;
    case 'DOCS_INCOMPLETE':
      return t`Docs incomplete`;
    case 'AT_CAPACITY':
      return t`At capacity`;
    case 'DO_NOT_USE':
      return t`Do not use`;
  }
}

export function employerStatusLongLabel(status: EmployerStatus): string {
  switch (status) {
    case 'READY':
      return t`Ready for students`;
    case 'DOCS_INCOMPLETE':
      return t`Documents incomplete`;
    case 'AT_CAPACITY':
      return t`At capacity`;
    case 'DO_NOT_USE':
      return t`Do not use`;
  }
}

export function employerStatusColorScheme(status: EmployerStatus): string {
  return COLOR_SCHEME_FOR_TONE[STATUS_PRESENTATION[status].tone];
}

export function EmployerStatusIndicator({
  status,
}: {
  status: EmployerStatus;
}): React.ReactElement {
  const { tone, icon: Icon } = STATUS_PRESENTATION[status];
  return (
    <HStack gap={1} color={tone} fontSize="sm">
      <Text>{employerStatusLongLabel(status)}</Text>
      <Icon size={iconSize.sm} />
    </HStack>
  );
}

export function EmployerStatusSummary({
  status,
  agreementExpiresAt,
  insuranceExpiresAt,
}: {
  status: EmployerStatus;
  agreementExpiresAt: Date | null;
  insuranceExpiresAt: Date | null;
}): React.ReactElement {
  const { tone, icon: Icon } = STATUS_PRESENTATION[status];
  return (
    <HStack gap={4} fontSize="sm" flexWrap="wrap">
      <HStack gap={1} color={tone} fontWeight="medium">
        <Icon size={iconSize.sm} />
        <Text>{employerStatusLongLabel(status)}</Text>
      </HStack>
      <HStack gap={1}>
        <Text fontWeight="semibold">
          <Trans>Agreement expiry</Trans>
        </Text>
        <Text color="fg.muted">{formatShortDate(agreementExpiresAt)}</Text>
      </HStack>
      <HStack gap={1}>
        <Text fontWeight="semibold">
          <Trans>Insurance expiry</Trans>
        </Text>
        <Text color="fg.muted">{formatShortDate(insuranceExpiresAt)}</Text>
      </HStack>
    </HStack>
  );
}
